import pluralize from 'pluralize';
import { renderValue } from 'src/data-capture/detected-entities/DataInput';
import extractCategoriesForDetection from 'src/data-capture/stores/helpers/extractCategoriesForDetection';
import { isEmpty } from 'src/data-capture/stores/helpers/isEmpty';
import { Field } from 'src/data-capture/stores/useDetectedEntitiesStore';
import { Detection, DetectionOwner, FieldTypeMetadata } from 'src/data-capture/types';
import { camelCaseOrSnakeCaseToWords } from 'src/data-capture/merge-review/ReviewHelperFunctions';

const NAMING_FIELDS = ['provider', 'scheme_name', 'employer_name', 'name', 'description'];
const FACTS_SHOWN = 2;

type Figure = { key: string; amount: number; metadata: FieldTypeMetadata };

export type FoundItem = {
  id: string;
  entityType: string;
  label: string;
  name: string | null;
  headline: string | null;
  /** A line of what else is known, so a row says more than its name before it is opened. */
  facts: string | null;
  /** How a shared item is split, such as "50% each". */
  sharing: string | null;
  figure: Figure | null;
  fields: { key: string; label: string; value: string }[];
};

export type FoundSection = {
  key: string;
  title: string;
  /** Null for the section that gathers kinds found only once, whose rows each say what they are. */
  entityType: string | null;
  items: FoundItem[];
  total: string | null;
  totalAmount: number | null;
};

/** The name that tells two items of one kind apart: "Aviva" and "Royal London" among pensions. */
function namingField(detection: Detection): { key: string; value: string } | null {
  const fields = detection.entity.fields;
  const key = NAMING_FIELDS.find(
    candidate => typeof fields[candidate] === 'string' && fields[candidate].trim().length > 0,
  );
  return key ? { key, value: fields[key] as string } : null;
}

function display(field: Field): string {
  return String(renderValue(field.value as React.ReactNode, field.metadata));
}

/** Whole pounds read more easily at a glance, so pence show only when there are some. */
function figureText(amount: number, metadata: FieldTypeMetadata): string {
  if (metadata.type === 'MONEY' && Number.isInteger(amount)) {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: metadata.currency ?? 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  }
  return String(renderValue(amount, metadata));
}

function sharing(owners: DetectionOwner[] | null | undefined): string | null {
  if (!owners || owners.length < 2) return null;
  const shares = owners.map(owner => owner.ownership_percentage);
  if (shares.some(share => share === null || share === undefined)) return 'Joint';
  return new Set(shares).size === 1
    ? `${shares[0]}% each`
    : shares.map(share => `${share}%`).join(' / ');
}

/** One row of "What we found": what it is, its figure, and the same field labels the review uses. */
export function toFoundItem(detection: Detection): FoundItem {
  const reviewed = Object.values(extractCategoriesForDetection(detection).new?.fieldsByKey ?? {});
  const shown = reviewed.filter(field => !isEmpty(field.value) && display(field) !== '-');
  const named = namingField(detection);
  const figureField = shown.find(field => field.metadata?.type === 'MONEY');
  const amount = Number(figureField?.value);
  const figure =
    figureField?.metadata && Number.isFinite(amount)
      ? { key: figureField.key, amount, metadata: figureField.metadata }
      : null;
  const fields = shown.map(field => ({
    key: field.key,
    label: field.metadata?.label ?? camelCaseOrSnakeCaseToWords(field.key),
    value: display(field),
  }));
  const facts = fields
    .filter(field => field.key !== named?.key && field.key !== figureField?.key)
    .slice(0, FACTS_SHOWN)
    .map(field => `${field.label}: ${field.value}`)
    .join(' · ');
  let headline: string | null = null;
  if (figure) headline = figureText(figure.amount, figure.metadata);
  else if (figureField) headline = display(figureField);
  return {
    id: detection.id,
    entityType: detection.entityType,
    label: detection.entityLabel,
    name: named?.value ?? null,
    headline,
    facts: facts || null,
    sharing: sharing(detection.resolvedOwners),
    figure,
    fields,
  };
}

/** A section's total, only when every item's figure is the same field, so like is added to like. */
function totalOf(items: FoundItem[]): { total: string; amount: number } | null {
  const figures = items.flatMap(item => (item.figure ? [item.figure] : []));
  if (figures.length !== items.length || new Set(figures.map(figure => figure.key)).size !== 1) {
    return null;
  }
  const amount = figures.reduce((sum, figure) => sum + figure.amount, 0);
  return { total: figureText(amount, figures[0].metadata), amount };
}

/**
 * Each kind found more than once gets a section titled by its kind, so the kind can't be pushed off the screen
 * by a long name. Kinds found once are gathered into one last section, where each row names its own kind.
 */
export function sectionsOf(items: FoundItem[], othersTitle: string): FoundSection[] {
  const byLabel = new Map<string, FoundItem[]>();
  items.forEach(item => byLabel.set(item.label, [...(byLabel.get(item.label) ?? []), item]));
  const groups = [...byLabel];
  const typed = groups
    .filter(([, grouped]) => grouped.length > 1)
    .map(([label, grouped]): FoundSection => {
      const total = totalOf(grouped);
      return {
        key: label,
        title: pluralize(label),
        entityType: grouped[0].entityType,
        items: grouped,
        total: total?.total ?? null,
        totalAmount: total?.amount ?? null,
      };
    });
  const singles = groups
    .filter(([, grouped]) => grouped.length === 1)
    .flatMap(([, grouped]) => grouped);
  if (singles.length === 0) return typed;
  return [
    ...typed,
    {
      key: 'others',
      title: othersTitle,
      entityType: null,
      items: singles,
      total: null,
      totalAmount: null,
    },
  ];
}
